import { Form, Input, Select } from 'antd';
import useTeams from '@hooks/useTeams';
import {
    EditCoachForm,
    InviteUserToTeamBody,
    STATUS,
    TEAM_ROLE,
    TeamAthlete,
} from '@type/db-types';
import invariant from 'tiny-invariant';
import { useMemo, useState } from 'react';
import { inviteUserToTeam } from '@modules/api';
import { updateUserAndMembership } from '@modules/supabase/users';
import LoadingButton from '@atoms/LoadingButton';

interface Props {
    onSuccess: () => void;
    editingUser?: TeamAthlete | boolean | undefined;
}
// FIXME: enable MaterialTable row level editing throughout the app!
const AddCoachesForm = ({ editingUser, onSuccess }: Props) => {
    const [loading, setLoading] = useState(false);
    const isExistingUser = !!(editingUser as TeamAthlete)?.id;
    const [form] = Form.useForm();

    const handleRoleChange = (role: TEAM_ROLE) => {
        form.setFieldsValue({ role });
    };

    const initialValues = useMemo(
        () => (isExistingUser ? (editingUser as TeamAthlete) : undefined),
        [editingUser, isExistingUser],
    );
    const { loading: teamsLoading, selectedTeam } = useTeams();

    const onFinish = async (values: EditCoachForm) => {
        invariant(selectedTeam, 'Team must be selected to add a coach');
        setLoading(true);

        const invite: InviteUserToTeamBody = {
            teamStatus: STATUS.INVITED,
            ...values,
            // role: TEAM_ROLE.COACH,
            teamId: selectedTeam.id as string, // team id is present when editing your team
        };
        // FIXME: implement this, update athlete logic from addWrestlerForm.tsx
        if (isExistingUser) {
            const user: Partial<Omit<TeamAthlete, 'weight'>> = {
                ...initialValues,
                ...invite,
                id: (editingUser as TeamAthlete)?.userId || undefined,
            };
            await updateUserAndMembership(user);
            setLoading(false);
            onSuccess();
        } else {
            inviteUserToTeam(invite)
                .then((result) => {
                    if (result?.userId) {
                        console.log('User invited successfully', result);
                        onSuccess();
                    } else {
                        console.error('Error inviting user', result);
                    }
                })
                .finally(() => setLoading(false));
        }
    };

    return (
        <div className="form-add-coach-container">
            <Form
                form={form}
                name="addCoachesForm"
                onFinish={onFinish}
                autoComplete="off"
                layout={'vertical'}
                initialValues={initialValues}
            >
                <div className="row">
                    <div className="col-md-6">
                        <Form.Item
                            label="First Name"
                            name="firstName"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please input your first name!',
                                },
                            ]}
                        >
                            <Input size="large" placeholder="Enter First Name" />
                        </Form.Item>
                    </div>

                    <div className="col-md-6">
                        <Form.Item
                            label="Last Name"
                            name="lastName"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please input your Last name!',
                                },
                            ]}
                        >
                            <Input size="large" placeholder="Enter Last Name" />
                        </Form.Item>
                    </div>
                    <div className="col-md-6">
                        <Form.Item
                            label="role"
                            name="role"
                            rules={[
                                {
                                    required: true,
                                    message: 'pick a role for the coach',
                                },
                            ]}
                        >
                            <Select placeholder="Coach's Role" onChange={handleRoleChange}>
                                <Select.Option value={TEAM_ROLE.ATHLETE}>Athlete</Select.Option>
                                <Select.Option value={TEAM_ROLE.PARENT}>Parent</Select.Option>
                                <Select.Option value={TEAM_ROLE.COACH}>Coach</Select.Option>
                                <Select.Option value={TEAM_ROLE.OWNER}>Owner</Select.Option>
                            </Select>
                        </Form.Item>
                    </div>

                    <div className="col-12">
                        <Form.Item
                            label="Email"
                            name="email"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please input your email',
                                },
                                {
                                    type: 'email',
                                    message: 'The input is not valid email',
                                },
                            ]}
                        >
                            <Input
                                size="large"
                                placeholder="Enter email"
                                disabled={isExistingUser}
                            />
                        </Form.Item>
                    </div>
                </div>
                <div className="col-12  mt-3">
                    <Form.Item>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            loading={loading || teamsLoading}
                        >
                            {isExistingUser ? 'Update Coach info' : 'Invite coach'}
                        </LoadingButton>
                    </Form.Item>
                </div>
            </Form>
        </div>
    );
};

export default AddCoachesForm;
